import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { MdOutlineCalendarMonth, MdOutlineFileDownload, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useToast } from '@podus/ui';
import RangeChips from '@/components/RangeChips';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import { api, ApiError, downloadCsv } from '@/lib/api';
import { formatDate, formatNumber } from '@/lib/format';
import type { DailyPayload, DailyRow, RangePreset } from '@/types';

/**
 * Referrals per ambassador, per day.
 *
 * The overview aggregates a whole range into single figures and the per-
 * ambassador page charts one person over time; this is the cut that answers
 * "who brought in what, and when" across the programme.
 */
export default function DailyBreakdown() {
    const toast = useToast();
    const [searchParams] = useSearchParams();

    // Deep-linkable: the ambassador detail page links here pre-filtered.
    const ambassadorId = searchParams.get('ambassadorId');

    const [preset, setPreset] = useState<RangePreset>('30d');
    const [payload, setPayload] = useState<DailyPayload | null>(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                preset,
                page: String(page),
                limit: '50',
                ...(ambassadorId ? { ambassadorId } : {}),
            });
            const res = await api.get<DailyPayload>(`/ambassador/admin/daily?${query.toString()}`);
            setPayload(res.response);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not load the daily breakdown');
        } finally {
            setLoading(false);
        }
    }, [preset, page, ambassadorId, toast]);

    useEffect(() => {
        void load();
    }, [load]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const query = new URLSearchParams({
                type: 'daily',
                preset,
                ...(ambassadorId ? { ambassadorId } : {}),
            });
            await downloadCsv(`/ambassador/admin/export?${query.toString()}`, 'podus-daily.csv');
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not prepare that export');
        } finally {
            setExporting(false);
        }
    };

    const rows: DailyRow[] = payload?.data ?? [];
    const totals = payload?.totals;
    const pagination = payload?.pagination;

    // Group consecutive rows so each date is announced once instead of repeating
    // down the first column.
    let lastDate = '';

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 lg:px-8 lg:py-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 className="text-[22px] font-bold text-secondary">Daily breakdown</h2>
                    <p className="mt-1 text-[13px] text-[#747474]">
                        Referrals per ambassador, per day.
                        {ambassadorId && (
                            <>
                                {' '}
                                Filtered to one ambassador —{' '}
                                <Link to="/admin/daily" className="font-semibold text-primary">
                                    show everyone
                                </Link>
                            </>
                        )}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <RangeChips
                        value={preset}
                        onChange={(next) => {
                            setPreset(next);
                            setPage(1);
                        }}
                        disabled={loading}
                    />
                    <button
                        type="button"
                        onClick={handleExport}
                        disabled={exporting || rows.length === 0}
                        className="flex items-center gap-2 rounded-lg border border-[#E0E0E0] bg-white px-4 py-2.5 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-50"
                    >
                        <MdOutlineFileDownload size={18} />
                        {exporting ? 'Preparing…' : 'Export CSV'}
                    </button>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard label="Link clicks" value={formatNumber(totals?.clicks ?? 0)} loading={loading} />
                <StatCard label="Signups" value={formatNumber(totals?.signups ?? 0)} accent loading={loading} />
                <StatCard label="Activated" value={formatNumber(totals?.activated ?? 0)} loading={loading} />
                <StatCard
                    label="Pending review"
                    value={formatNumber(totals?.pending ?? 0)}
                    hint="Excluded from signups"
                    loading={loading}
                />
            </div>

            <div className="mt-5">
                {loading ? (
                    <div className="flex flex-col gap-2">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <div key={index} className="skeleton-shimmer h-12 w-full rounded-lg" />
                        ))}
                    </div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        Icon={MdOutlineCalendarMonth}
                        title="No activity in this period"
                        description="Days with no clicks and no signups aren't listed. Try a wider date range."
                    />
                ) : (
                    <>
                        <div className="overflow-x-auto rounded-xl border border-[#E0E0E0] bg-white">
                            <table className="w-full min-w-[720px] text-left">
                                <thead>
                                    <tr className="border-b border-[#EEEEEE] text-[12px] uppercase tracking-wide text-[#9a9a9a]">
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Date
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Ambassador
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Code
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Clicks
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Signups
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Activated
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Pending
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const isNewDate = row.date !== lastDate;
                                        lastDate = row.date;

                                        return (
                                            <tr
                                                key={`${row.date}-${row.ambassadorId}`}
                                                className={`border-b border-[#f5f5f5] last:border-0 ${
                                                    isNewDate ? 'border-t-[#EEEEEE]' : ''
                                                }`}
                                            >
                                                <td className="whitespace-nowrap px-5 py-3.5 text-[14px] text-[#4E4D4D]">
                                                    {isNewDate ? formatDate(row.date) : ''}
                                                </td>
                                                <td className="px-5 py-3.5">
                                                    <Link
                                                        to={`/admin/ambassadors/${row.ambassadorId}`}
                                                        className="text-[14px] font-semibold text-secondary hover:text-primary"
                                                    >
                                                        {row.name ?? row.email}
                                                    </Link>
                                                </td>
                                                <td className="px-5 py-3.5 font-mono text-[13px] text-[#4E4D4D]">
                                                    {row.code ?? '—'}
                                                </td>
                                                <td className="px-5 py-3.5 text-[14px] tabular-nums text-[#4E4D4D]">
                                                    {formatNumber(row.clicks)}
                                                </td>
                                                <td className="px-5 py-3.5 text-[14px] font-semibold tabular-nums text-secondary">
                                                    {formatNumber(row.signups)}
                                                </td>
                                                <td className="px-5 py-3.5 text-[14px] tabular-nums text-[#4E4D4D]">
                                                    {formatNumber(row.activated)}
                                                </td>
                                                <td className="px-5 py-3.5 text-[14px] tabular-nums text-[#4E4D4D]">
                                                    {row.pending > 0 ? (
                                                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[12px] font-semibold text-amber-700">
                                                            {formatNumber(row.pending)}
                                                        </span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {pagination && pagination.totalPages > 1 && (
                            <div className="mt-4 flex items-center justify-between">
                                <p className="text-[13px] text-[#747474]">
                                    Page {pagination.page} of {pagination.totalPages} · {pagination.total} rows
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        title="Previous page"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E0E0E0] text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                                    >
                                        <MdChevronLeft size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        title="Next page"
                                        disabled={page >= pagination.totalPages}
                                        onClick={() => setPage((p) => p + 1)}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E0E0E0] text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                                    >
                                        <MdChevronRight size={18} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
